import { createServerFn } from "@tanstack/react-start";
import type {
  ChannelResponse,
  CreateChannelRequest,
  PreferenceResponse,
  SetPreferenceRequest,
  UpdateChannelRequest,
} from "@/lib/api/generated";
import {
  createNotificationChannelNotificationsChannelsPost,
  deleteNotificationChannelNotificationsChannelsChannelIdDelete,
  getNotificationLogsNotificationsLogsWorkspaceIdGet,
  listNotificationChannelsNotificationsChannelsWorkspaceIdGet,
  setNotificationPreferenceNotificationsPreferencesPut,
  updateNotificationChannelNotificationsChannelsChannelIdPut,
} from "@/lib/api/generated/notifications/notifications";
import { logError, logInfo, logWarn } from "@/lib/structured-log";

/**
 * 创建通知渠道
 *
 * 调用方式（客户端）:
 * await createNotificationChannelServer({
 *   data: { workspaceId: "xxx", payload: { channel_type: "lark", name: "...", webhook_url: "..." } }
 * })
 */
export const createNotificationChannelServer = createServerFn({ method: "POST" })
  .validator((data: { workspaceId: string; payload: CreateChannelRequest }) => data)
  .handler(async ({ data }): Promise<ChannelResponse> => {
    try {
      const response = await createNotificationChannelNotificationsChannelsPost(data.payload, {
        workspace_id: data.workspaceId,
      });

      if (response.status !== 201) {
        logError("notifications.channel.create_failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          channel_type: data.payload.channel_type,
        });
        throw new Error(`Failed to create notification channel: ${response.status}`);
      }

      logInfo("notifications.channel.create_success", {
        channel_id: response.data.id,
        workspace_id: data.workspaceId,
        channel_type: data.payload.channel_type,
      });
      return response.data;
    } catch (error) {
      logError("notifications.channel.create_error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data?.workspaceId || "unknown",
      });
      throw error;
    }
  });

/**
 * 获取工作区的所有通知渠道
 */
export const listNotificationChannelsServer = createServerFn({ method: "GET" })
  .validator((data: { workspaceId: string }) => data)
  .handler(async ({ data }): Promise<ChannelResponse[]> => {
    try {
      const response = await listNotificationChannelsNotificationsChannelsWorkspaceIdGet(
        data.workspaceId,
      );

      if (response.status !== 200) {
        logError("notifications.channel.list_failed", {
          status: response.status,
          workspace_id: data.workspaceId,
        });
        throw new Error(`Failed to list notification channels: ${response.status}`);
      }

      logInfo("notifications.channel.list_success", {
        workspace_id: data.workspaceId,
        count: response.data.length,
      });
      return response.data;
    } catch (error) {
      logError("notifications.channel.list_error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
      });
      throw error;
    }
  });

/**
 * 更新通知渠道
 */
export const updateNotificationChannelServer = createServerFn({ method: "POST" })
  .validator((data: { channelId: string; payload: UpdateChannelRequest }) => data)
  .handler(async ({ data }): Promise<ChannelResponse> => {
    try {
      const response = await updateNotificationChannelNotificationsChannelsChannelIdPut(
        data.channelId,
        data.payload,
      );

      if (response.status !== 200) {
        logError("notifications.channel.update_failed", {
          status: response.status,
          channel_id: data.channelId,
        });
        throw new Error(`Failed to update notification channel: ${response.status}`);
      }

      logInfo("notifications.channel.update_success", {
        channel_id: data.channelId,
      });
      return response.data;
    } catch (error) {
      logError("notifications.channel.update_error", {
        error: error instanceof Error ? error.message : "unknown-error",
        channel_id: data.channelId,
      });
      throw error;
    }
  });

/**
 * 删除通知渠道
 */
export const deleteNotificationChannelServer = createServerFn({ method: "POST" })
  .validator((data: { channelId: string }) => data)
  .handler(async ({ data }): Promise<void> => {
    try {
      const response = await deleteNotificationChannelNotificationsChannelsChannelIdDelete(
        data.channelId,
      );

      if (response.status !== 204) {
        logError("notifications.channel.delete_failed", {
          status: response.status,
          channel_id: data.channelId,
        });
        throw new Error(`Failed to delete notification channel: ${response.status}`);
      }

      logInfo("notifications.channel.delete_success", {
        channel_id: data.channelId,
      });
    } catch (error) {
      logError("notifications.channel.delete_error", {
        error: error instanceof Error ? error.message : "unknown-error",
        channel_id: data.channelId,
      });
      throw error;
    }
  });

/**
 * 设置通知偏好
 */
export const setNotificationPreferenceServer = createServerFn({ method: "POST" })
  .validator((data: { workspaceId: string; payload: SetPreferenceRequest }) => data)
  .handler(async ({ data }): Promise<PreferenceResponse> => {
    try {
      const response = await setNotificationPreferenceNotificationsPreferencesPut(data.payload, {
        workspace_id: data.workspaceId,
      });

      if (response.status !== 200) {
        logError("notifications.preference.set_failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          event_type: data.payload.event_type,
        });
        throw new Error(`Failed to set notification preference: ${response.status}`);
      }

      logInfo("notifications.preference.set_success", {
        preference_id: response.data.id,
        workspace_id: data.workspaceId,
        event_type: data.payload.event_type,
      });
      return response.data;
    } catch (error) {
      logError("notifications.preference.set_error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
      });
      throw error;
    }
  });

/**
 * 获取用户的通知偏好
 * Returns event_type -> channel_ids[] mapping
 */
export const getUserNotificationPreferencesServer = createServerFn({ method: "GET" })
  .validator((data: { workspaceId: string }) => data)
  .handler(async ({ data }): Promise<Record<string, string[]>> => {
    try {
      const { getUserPreferencesNotificationsPreferencesWorkspaceIdGet } = await import(
        "@/lib/api/generated/notifications/notifications"
      );
      const response = await getUserPreferencesNotificationsPreferencesWorkspaceIdGet(
        data.workspaceId,
      );

      if (response.status !== 200) {
        logError("notifications.preferences.get_failed", {
          status: response.status,
          workspace_id: data.workspaceId,
        });
        throw new Error(`Failed to get user preferences: ${response.status}`);
      }

      logInfo("notifications.preferences.get_success", {
        workspace_id: data.workspaceId,
      });
      return response.data;
    } catch (error) {
      logError("notifications.preferences.get_error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
      });
      throw error;
    }
  });

/**
 * 查询通知日志
 */
export const getNotificationLogsServer = createServerFn({ method: "GET" })
  .validator(
    (data: { workspaceId: string; eventType?: string; status?: string; limit?: number }) => data,
  )
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  .handler(async ({ data }): Promise<any> => {
    try {
      const params: Record<string, string | number> = {};
      if (data.eventType) params.event_type = data.eventType;
      if (data.status) params.status = data.status;
      if (data.limit !== undefined) params.limit = data.limit;

      const response = await getNotificationLogsNotificationsLogsWorkspaceIdGet(
        data.workspaceId,
        params,
      );

      if (response.status !== 200) {
        logError("notifications.logs.get_failed", {
          status: response.status,
          workspace_id: data.workspaceId,
        });
        throw new Error(`Failed to get notification logs: ${response.status}`);
      }

      logInfo("notifications.logs.get_success", {
        workspace_id: data.workspaceId,
        count: response.data.length,
      });
      return response.data;
    } catch (error) {
      logError("notifications.logs.get_error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
      });
      throw error;
    }
  });

/**
 * 批量设置通知偏好
 */
export const batchSetNotificationPreferencesServer = createServerFn({
  method: "POST",
})
  .validator((data: { workspaceId: string; preferences: SetPreferenceRequest[] }) => data)
  .handler(async ({ data }): Promise<PreferenceResponse[]> => {
    try {
      const results: PreferenceResponse[] = [];

      for (const preference of data.preferences) {
        const response = await setNotificationPreferenceNotificationsPreferencesPut(preference, {
          workspace_id: data.workspaceId,
        });

        if (response.status !== 200) {
          logWarn("notifications.preference.batch_set_item_failed", {
            status: response.status,
            workspace_id: data.workspaceId,
            event_type: preference.event_type,
          });
          // 继续处理其他偏好，但记录失败
          continue;
        }

        results.push(response.data);
      }

      logInfo("notifications.preference.batch_set_success", {
        workspace_id: data.workspaceId,
        total: data.preferences.length,
        succeeded: results.length,
      });

      return results;
    } catch (error) {
      logError("notifications.preference.batch_set_error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
      });
      throw error;
    }
  });
